import React from "react";
import styled from "@emotion/styled";
import { THealthDay } from "@services/devicesService";

/**
 * Only the three fields the line and its bands actually read. Declared as a
 * subset of THealthDay so the devices screen keeps passing its cells unchanged,
 * while callers with a trimmed payload (the admin list) can pass theirs.
 */
export type TSparklineDay = Pick<
  THealthDay,
  "day" | "severity" | "avgValue" | "avgFreq"
>;

/**
 * Per-day sensor health strip: a mean-sensor trend line over shaded bands
 * marking the days the monitors judged broken.
 *
 * Hand-rolled SVG rather than recharts on purpose. Every other chart in this
 * app is >=180px tall inside a ResponsiveContainer, which is the right tool for
 * a dashboard panel and the wrong one for a 32px decoration repeated on every
 * row of a list — six ResponsiveContainers all observing resize is a real cost
 * for something that draws one polyline.
 *
 * The line is the mean of the day's working points on the channel the device
 * actually uses — `avgFreq` for a FREQUENCY-mode potato device, `avgValue`
 * otherwise. The backend resolves that and sends it as `channel`; picking it
 * here from `sensorMode` would duplicate a mapping the classifier owns.
 *
 * It is deliberately NOT what decides the shading: the bands come from the
 * backend's per-day verdict, which is computed from fractions, medians and
 * modulation. A mean is far too blunt to judge a sensor by — it is here to give
 * the eye a shape, and the bands are the actual signal.
 */

const BAND: Record<string, string> = {
  critical: "rgba(248, 113, 113, 0.28)",
  advisory: "rgba(253, 224, 71, 0.35)",
};

const LINE_OK = "#68AD18";
const LINE_DEAD = "#C9CDD2";

const Root = styled.svg`
  display: block;
  overflow: visible;
`;

/** Percentile band the y-axis is fitted to, once there are enough days. */
const CLAMP_LO = 0.1;
const CLAMP_HI = 0.9;
/** Below this many reporting days a percentile is noise; use the true range. */
const CLAMP_MIN_DAYS = 8;
/** Headroom above and below the band, as a fraction of its span. */
const CLAMP_PAD = 0.15;

/** Linear-interpolated quantile over an ascending array. */
const quantile = (sorted: number[], q: number): number => {
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
};

/**
 * The value range the line is drawn against.
 *
 * Fitting min..max sounds obviously right and is the reason this chart once
 * read as a flat zero for a working machine: a single day whose mean ran 20x
 * normal — a saturated channel, a miscalibrated morning — sets `max`, and every
 * other day in the window then lands within a pixel of the floor. So past
 * CLAMP_MIN_DAYS the axis is fitted to the p10..p90 band instead and outliers
 * are clamped to the edge. They stay visible as a pegged run, and the ordinary
 * days keep the vertical space they need to show a shape.
 *
 * Under CLAMP_MIN_DAYS there is no distribution to speak of, so the true range
 * is both safer and more honest — with five days, "the top one is an outlier"
 * is a guess.
 */
const domainOf = (values: number[]): { min: number; max: number } => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (values.length < CLAMP_MIN_DAYS) return { min, max };

  const sorted = [...values].sort((a, b) => a - b);
  const lo = quantile(sorted, CLAMP_LO);
  const hi = quantile(sorted, CLAMP_HI);
  // A band of zero width means the middle 80% of days are identical; padding it
  // would invent a scale, so fall back to the true range.
  if (!(hi > lo)) return { min, max };

  const pad = (hi - lo) * CLAMP_PAD;
  return { min: Math.max(min, lo - pad), max: Math.min(max, hi + pad) };
};

interface IProps {
  days: TSparklineDay[];
  /** Channel to plot, from the health payload. Defaults to the common case. */
  channel?: "value" | "freq";
  width?: number;
  height?: number;
  /**
   * Draw the trend in grey instead of green — for a device that has gone
   * quiet, where the shape is history rather than a live reading.
   */
  muted?: boolean;
  /** Index of the day under the cursor, for the caller's tooltip. */
  onHoverDay?: (index: number | null) => void;
  className?: string;
}

const Sparkline: React.FC<IProps> = ({
  days,
  channel = "value",
  width = 140,
  height = 34,
  muted = false,
  onHoverDay,
  className,
}) => {
  if (days.length === 0) {
    return (
      <Root width={width} height={height} className={className} aria-hidden>
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke={LINE_DEAD}
          strokeWidth={1.5}
          strokeDasharray="3 3"
        />
      </Root>
    );
  }

  const slotW = width / days.length;
  const padY = 3;
  const usableH = height - padY * 2;

  const levelOf = (d: TSparklineDay): number | null => {
    const v = channel === "freq" ? d.avgFreq : d.avgValue;
    return v !== null && v !== undefined && Number.isFinite(v) ? v : null;
  };

  // Normalise over the days that actually reported. A day with no working
  // points has no mean at all, and plotting it as zero would invent a dropout
  // that never happened — those days break the line instead.
  const values = days
    .map(levelOf)
    .filter((v): v is number => v !== null);
  const hasSignal = values.length > 0;
  const { min, max } = hasSignal ? domainOf(values) : { min: 0, max: 1 };
  // A perfectly flat series would divide by zero; centre it instead.
  const span = max - min || 1;

  const pointAt = (d: TSparklineDay, i: number): [number, number] | null => {
    const v = levelOf(d);
    if (v === null) return null;
    const x = i * slotW + slotW / 2;
    // Clamped, not dropped: an outlier day is real data and pegging it at the
    // edge says so, where omitting it would open a gap that reads as silence.
    const clamped = Math.min(max, Math.max(min, v));
    const y =
      max === min
        ? height / 2
        : padY + usableH - ((clamped - min) / span) * usableH;
    return [x, y];
  };

  // Split into runs of consecutive reporting days so gaps stay gaps.
  const segments: string[] = [];
  let current: string[] = [];
  days.forEach((d, i) => {
    const p = pointAt(d, i);
    if (p) {
      current.push(`${p[0].toFixed(1)},${p[1].toFixed(1)}`);
    } else if (current.length) {
      segments.push(current.join(" "));
      current = [];
    }
  });
  if (current.length) segments.push(current.join(" "));

  return (
    <Root
      width={width}
      height={height}
      className={className}
      onMouseLeave={() => onHoverDay?.(null)}
      role="img"
    >
      {days.map((d, i) => {
        const fill = BAND[d.severity];
        if (!fill) return null;
        return (
          <rect
            key={`band-${d.day}`}
            x={i * slotW}
            y={0}
            width={slotW}
            height={height}
            fill={fill}
          />
        );
      })}

      {segments.map((pts, i) =>
        // A run of one day has no line to draw — show the point.
        pts.includes(" ") ? (
          <polyline
            key={`seg-${i}`}
            points={pts}
            fill="none"
            stroke={hasSignal && !muted ? LINE_OK : LINE_DEAD}
            strokeWidth={1.5}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : (
          <circle
            key={`seg-${i}`}
            cx={Number(pts.split(",")[0])}
            cy={Number(pts.split(",")[1])}
            r={1.5}
            fill={muted ? LINE_DEAD : LINE_OK}
          />
        )
      )}

      {/* Transparent hit targets on top, so hover works over gaps too. */}
      {onHoverDay &&
        days.map((d, i) => (
          <rect
            key={`hit-${d.day}`}
            x={i * slotW}
            y={0}
            width={slotW}
            height={height}
            fill="transparent"
            onMouseEnter={() => onHoverDay(i)}
          />
        ))}
    </Root>
  );
};

export default Sparkline;
